import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Skills.css';

const Skills = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const skillCategories = [
    {
      title: 'Languages',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="16 18 22 12 16 6"/>
          <polyline points="8 6 2 12 8 18"/>
        </svg>
      ),
      skills: [
        { name: 'Python', level: 95 },
        { name: 'C/C++', level: 85 },
        { name: 'R', level: 80 },
        { name: 'SQL', level: 90 },
        { name: 'Bash', level: 75 },
        { name: 'NoSQL', level: 80 },
        { name: 'C#', level: 70 },
        { name: 'MATLAB', level: 75 },
      ],
    },
    {
      title: 'ML & Data Science',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      ),
      skills: [
        { name: 'Scikit-Learn', level: 95 },
        { name: 'Deep Learning', level: 85 },
        { name: 'Generative AI', level: 88 },
        { name: 'Computer Vision', level: 82 },
        { name: 'OpenCV', level: 85 },
        { name: 'Time Series', level: 80 },
        { name: 'Data Mining', level: 88 },
        { name: 'NLP', level: 78 },
      ],
    },
    {
      title: 'Tools & Platforms',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
          <line x1="8" y1="21" x2="16" y2="21"/>
          <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
      ),
      skills: [
        { name: 'AWS (EC2, RDS, Lambda, SQS)', level: 90 },
        { name: 'Docker', level: 85 },
        { name: 'Databricks', level: 80 },
        { name: 'Git', level: 90 },
        { name: 'BigID', level: 85 },
        { name: 'Spark', level: 75 },
      ],
    },
    {
      title: 'Databases',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <ellipse cx="12" cy="5" rx="9" ry="3"/>
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
        </svg>
      ),
      skills: [
        { name: 'MySQL', level: 90 },
        { name: 'PostgreSQL', level: 85 },
        { name: 'IBM DB2', level: 80 },
        { name: 'Oracle', level: 78 },
        { name: 'MariaDB', level: 82 },
        { name: 'MongoDB', level: 75 },
      ],
    },
    {
      title: 'Frameworks & Dev',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      ),
      skills: [
        { name: 'MERN Stack', level: 85 },
        { name: 'React', level: 82 },
        { name: 'Node.js', level: 80 },
        { name: 'REST APIs', level: 90 },
        { name: 'Agile/Scrum', level: 88 },
      ],
    },
    {
      title: 'Domain Expertise',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      skills: [
        { name: 'Data Privacy (GDPR, CCPA, HIPAA)', level: 95 },
        { name: 'Differential Privacy', level: 92 },
        { name: 'ETL Pipelines', level: 85 },
        { name: 'Serverless Architecture', level: 82 },
        { name: 'Image Processing', level: 88 },
        { name: 'Product Management', level: 85 },
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section id="skills" className="skills section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Technical Skills</h2>
          <p className="section-subtitle">
            Full-stack ML engineering capabilities across languages, frameworks, and platforms
          </p>
        </motion.div>

        <motion.div
          className="skills-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {skillCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              className="skill-category"
              variants={itemVariants}
            >
              <div className="category-header">
                <div className="category-icon">
                  {category.icon}
                </div>
                <h3>{category.title}</h3>
              </div>
              
              <div className="skill-list">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skillIndex} className="skill-item">
                    <div className="skill-info">
                      <span className="skill-name">{skill.name}</span>
                      <span className="skill-level">{skill.level}%</span>
                    </div>
                    <div className="skill-bar">
                      <motion.div
                        className="skill-progress"
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                        transition={{ duration: 1, delay: 0.3 + skillIndex * 0.1 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Quick Tags */}
        <motion.div
          className="skills-tags"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3>Also Experienced With</h3>
          <div className="tags-container">
            {[
              'Jira', 'Confluence', 'Atlassian', 'Android', 'Raspberry Pi',
              'Linux', 'Windows', 'Mac', 'Requirements Analysis', 
              'Process & Project Management', 'Data Analytics', 'B2B Sales',
            ].map((tag, index) => (
              <span key={index} className="skill-tag">{tag}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;

